import "./VerifyToDo.scss";
import Card from "../../../../../components/base/Card/Card";
import AppButton from "../../../../../components/base/Button/Button";
import MiniCheckBox from "../../../../../components/modules/MiniCheckBox/MiniCheckBox";
import { useEffect, useState } from "react";
import {
    fetchUserAddressByIdV1,
    fetchUserInformationByIdV1,
} from "../../../../../api/user";
import {
    createEhrUserByUserId,
    getRelatedEhrUsersByLastName,
    linkEhrUserByUserId,
} from "../../../../../api/ehrconnect";
import { updateIfollowedUpTodo } from "../../../../../redux/services/loggingServices";
import { useDispatch, useSelector } from "react-redux";
import {
    clearModalContent,
    setModalContent,
} from "../../../../../redux/modules/modalEvent";
import { Alert } from "antd";
import moment from "moment";
import { UserServiceSection } from "../../../constants/AppServerConfig";
import { noop } from "lodash";
import { Typography } from "src/latest/src/components";

interface Props {
    todo: any;
    onAMDVerified: () => void;
}

const VerifyToDo = ({ todo, onAMDVerified }: Props) => {
    const tenantLogo = useSelector(
        (state: any) => state?.configuration?.tenantLogo
    );
    const fetchingAppConfig = useSelector(
        (state: any) => state?.configuration?.fetchingAppConfig
    );
    const ehrName = useSelector((state: any) => state?.configuration?.ehrName);
    const todoId = todo.id;
    const userId = todo.user_id;
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [linkInProgress, setLinkInProgress] = useState(false);
    const [createInProgress, setCreateInProgress] = useState(false);

    const [amdUsers, setAMDUsers] = useState<any>([]);
    const [selectedAMDUser, setSelectedAMDUser] = useState<any>(null);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const dispatch = useDispatch();

    const selectAMDUser = (userID: any) => {
        setErrorMessage("");

        // radio selection mechanism
        const users = amdUsers.map((user: any) => {
            if (user.ehr_user_id === userID) {
                user.selected = !user.selected;

                if (user.selected) {
                    setSelectedAMDUser(user);
                } else {
                    setSelectedAMDUser(null);
                }
            } else {
                user.selected = false;
            }

            return user;
        });

        setAMDUsers(users);
    };

    useEffect(() => {
        (async () => {
            if (userId) {
                const currentUserResponse = await fetchUserInformationByIdV1(
                    userId,
                    UserServiceSection.profile_display,
                    'medium'
                );

                const currentUserAddress = await fetchUserAddressByIdV1(userId);

                currentUserResponse.birth_date = moment
                    .utc(new Date(currentUserResponse.birth_date))
                    .format("MM/DD/YYYY");

                setCurrentUser({
                    ...currentUserResponse,
                    ...{ address: currentUserAddress },
                });
            }
        })();
    }, [userId]);

    useEffect(() => {
        if (currentUser) {
            (async () => {
                setLoading(true);
                const usersResp = await getRelatedEhrUsersByLastName(
                    currentUser.last_name,
                    currentUser.id,
                    currentUser.birth_date
                );

                setAMDUsers(usersResp.users || []);
                setLoading(false);
            })();
        }
    }, [currentUser]);

    const linkUser = async (ehrUserId: string, userId: string) => {
        setLinkInProgress(true);
        setErrorMessage("");

        try {
            await linkEhrUserByUserId(ehrUserId, userId);

            createFollowUp();
        } catch (e: any) {
            setErrorMessage(
                e?.response?.data?.message || "Error, creating this user."
            );
        }

        setLinkInProgress(false);
    };

    const createUser = async (userId: string) => {
        setCreateInProgress(true);
        setErrorMessage("");

        try {
            await createEhrUserByUserId(userId);

            createFollowUp();
        } catch (e: any) {
            setErrorMessage(
                e?.response?.data?.message || "Error, creating this user."
            );
        }

        setCreateInProgress(false);
    };

    const createFollowUp = async () => {
        const respObj = {
            follow_up_by: ["SELF"],
            note: "",
        };

        await updateIfollowedUpTodo(todoId, respObj);

        showSuccessMessage();
    };

    const showSuccessMessage = async () => {
        dispatch(clearModalContent());
        dispatch(
            setModalContent({
                modalContent: (
                    <Alert
                        message="Success"
                        description="AdvancedMD user was created and linked."
                        type="success"
                        showIcon={true}
                    />
                ),
                isGeneralPopup: true,
                confirmButtonLabel: "OK",
                onConfirmButtonClick: () => {
                    dispatch(clearModalContent());
                },
            })
        );

        await onAMDVerified();
    };

    return (
        <div className="verify-todo">
            <div className="container">
                <div className="row todo-content">
                    <div className="col-6 current-patient">
                        <p className="current-patient-title">
                            This patient may already exists in AdvancedMD
                        </p>

                        {currentUser && (
                            <Card className="current-patient-info">
                                <div className="text-start mb-4">
                                    {!fetchingAppConfig && (
                                        <img
                                            src={`${decodeURIComponent(
                                                tenantLogo
                                            )}`}
                                            alt="Tenant logo"
                                            className="d-inline-block mr-1 mb-1"
                                        />
                                    )}
                                </div>
                                <div className="patient-name text-start">
                                    {currentUser?.full_name}
                                </div>
                                <div className="preferred-name-left">
                                    {/* Adding blank for now - update it with preferred name */}
                                    Preferred Name :{" "}
                                    <b>
                                        {
                                            currentUser?.metadata?.[
                                            "preferred-first-name"
                                            ]
                                        }
                                    </b>
                                </div>
                                <div className="row mt-4">
                                    <div className="col-6 text-start">
                                        <div className="title">
                                            Date of birth
                                        </div>
                                        <div className="description">
                                            {currentUser?.birth_date}
                                        </div>
                                    </div>
                                    <div className="col-6 text-start">
                                        <div className="title">
                                            Phone number
                                        </div>
                                        <div className="description">
                                            {currentUser?.telephone_number}
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-6 text-start">
                                        <div className="title">Address</div>
                                        <div className="description text-lowercase">
                                            {currentUser?.address ? (
                                                <>
                                                    <p className="address">
                                                        {
                                                            currentUser?.address
                                                                ?.address
                                                        }
                                                        <br />
                                                        {
                                                            currentUser?.address
                                                                ?.city
                                                        }
                                                        ,{" "}
                                                        {
                                                            currentUser?.address
                                                                ?.state
                                                        }{" "}
                                                        {
                                                            currentUser?.address
                                                                ?.zipcode
                                                        }
                                                    </p>
                                                </>
                                            ) : (
                                                "Not Available"
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-6 text-start">
                                        <div className="title">Email</div>
                                        <div className="description text-wrap text-break text-lowercase">
                                            {currentUser?.email}
                                        </div>
                                    </div>
                                </div>{" "}
                            </Card>
                        )}

                        {!currentUser && (
                            <>
                                <div
                                    className="spinner-border text-secondary"
                                    role="status"
                                />
                                Fetching selected i2i user
                            </>
                        )}
                        <div className="information-message">
                            Please review the information on the right to
                            determine if this patient already exists in{" "}
                            {ehrName || "EHR"}. Selecting an existing patient
                            record and clicking "Link Accounts" will link this
                            account to that record. If you believe that this
                            patient does not already exist in {ehrName || "EHR"}
                            , click the button "Create New Account" to add a new
                            patient record to {ehrName || "EHR"}.
                        </div>
                    </div>
                    <div className="col-6 amd-patient">
                        {loading && (
                            <>
                                <div
                                    className="spinner-border text-secondary"
                                    role="status"
                                />
                                Fetching {ehrName || "EHR"} Users
                            </>
                        )}
                        <div className="amd-users">
                            {amdUsers?.map((user: any, index: number) => (
                                <Card
                                    key={user?.id}
                                    className={`${user.selected ? "border-selected" : ""
                                        } amd-patient-info ${index !== 0 ? "mt-4" : ""
                                        }`}
                                    onClick={() =>
                                        selectAMDUser(user.ehr_user_id)
                                    }
                                >
                                    <div className="text-start mb-2">
                                        <Typography
                                            color="color-27"
                                            weight="extra-bold"
                                            variant="size-20"
                                            className="d-inline-block"
                                        >
                                            AdvancedMD
                                        </Typography>
                                    </div>
                                    <div className="custom-checkbox">
                                        <MiniCheckBox
                                            text="This is the same patient"
                                            className="amd-user-selector"
                                            value={"same"}
                                            onClick={noop} // do nothing - not required
                                            isActive={user?.selected}
                                        />
                                    </div>
                                    <div className="patient-name text-start">
                                        {user?.first_name} {user?.last_name}
                                    </div>
                                    <div className="preferred-chart-container row">
                                        {/* The key does not exist - make changes in future */}
                                        {user?.preferred_name ? (
                                            <div className="preferred-name col-6 text-start">
                                                Preferred Name:{" "}
                                                <b>{user?.preferred_name}</b>
                                            </div>
                                        ) : (
                                            ""
                                        )}
                                        <div className="preferred-name col-6 text-start">
                                            Chart Number:{" "}
                                            <b>{user?.chart_number}</b>
                                        </div>
                                    </div>

                                    <div className="row mt-4">
                                        <div className="col-6 text-start">
                                            <div
                                                className={`title ${user?.dob ===
                                                        currentUser.birth_date
                                                        ? "matched"
                                                        : ""
                                                    }`}
                                            >
                                                Date of birth
                                            </div>
                                            <div className="description">
                                                {user?.dob}
                                            </div>
                                        </div>
                                        <div className="col-6 text-start">
                                            <div
                                                className={`title ${user?.phone ===
                                                        currentUser?.telephone_number
                                                        ? "matched"
                                                        : ""
                                                    }`}
                                            >
                                                Phone number
                                            </div>
                                            <div className="description">
                                                {user?.phone}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6 text-start">
                                            <div className={`title`}>
                                                Address
                                            </div>
                                            <div className="description text-lowercas">
                                                {user?.address &&
                                                    user?.address?.address2 ? (
                                                    <>
                                                        <p className="address">
                                                            {
                                                                user?.address
                                                                    ?.address2
                                                            }
                                                            <br />
                                                            {
                                                                user?.address
                                                                    ?.city
                                                            }{" "}
                                                            {
                                                                user?.address
                                                                    ?.state
                                                            }
                                                            ,{" "}
                                                            {user?.address?.zip}
                                                        </p>
                                                    </>
                                                ) : (
                                                    "Not Available"
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-6 text-start">
                                            <div
                                                className={`title ${user?.email?.toLowerCase() ===
                                                        currentUser?.email
                                                        ? "matched"
                                                        : ""
                                                    }`}
                                            >
                                                Email
                                            </div>
                                            <div className="description text-wrap text-break text-lowercase">
                                                {user?.email}
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                        <div
                            style={{
                                visibility: loading ? "hidden" : "visible",
                            }}
                        >
                            <hr className={`mt-5 ${!errorMessage && "mb-5"}`} />

                            {errorMessage && (
                                <Typography
                                    variant="size-16"
                                    color="color-6"
                                    weight="regular"
                                    className="text-start mb-5"
                                >
                                    {errorMessage}
                                </Typography>
                            )}
                            <div className="row btn-holder">
                                <div className="col-6 text-start">
                                    <AppButton
                                        variant="light"
                                        label="Create New Account"
                                        className="small-btn"
                                        disabled={
                                            loading ||
                                            selectedAMDUser ||
                                            linkInProgress
                                        }
                                        isProcessing={createInProgress}
                                        onClick={() => createUser(userId)}
                                    />
                                </div>
                                <div className="col-6">
                                    <AppButton
                                        variant="success"
                                        label="Link Account"
                                        disabled={
                                            loading ||
                                            !selectedAMDUser ||
                                            createInProgress
                                        }
                                        isProcessing={linkInProgress}
                                        onClick={() =>
                                            linkUser(
                                                selectedAMDUser?.ehr_user_id,
                                                userId
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyToDo;
